import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

const TOKEN_KEY = "ls_token";
const ADMIN_BACKUP_KEY = "ls_admin_token";
const IMPERSONATOR_KEY = "ls_impersonator_email";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  const [impersonator, setImpersonator] = useState(() => sessionStorage.getItem(IMPERSONATOR_KEY));

  const refresh = useCallback(async () => {
    // CRITICAL: If returning from Emergent OAuth callback, skip /auth/me — AuthCallback
    // will exchange the session_id and set the token before any protected route renders.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setReady(true);
      return;
    }
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser(false);
      setReady(true);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setUser(false);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      sessionStorage.removeItem(ADMIN_BACKUP_KEY);
      sessionStorage.removeItem(IMPERSONATOR_KEY);
      setImpersonator(null);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (email, password) => {
    try {
      const { data } = await api.post("/auth/register", { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(ADMIN_BACKUP_KEY);
    sessionStorage.removeItem(IMPERSONATOR_KEY);
    setImpersonator(null);
    setUser(false);
  };

  const impersonate = async (userId) => {
    try {
      const { data } = await api.post(`/admin/users/${userId}/impersonate`);
      // Backup admin token so we can return
      const adminToken = localStorage.getItem(TOKEN_KEY);
      sessionStorage.setItem(ADMIN_BACKUP_KEY, adminToken);
      sessionStorage.setItem(IMPERSONATOR_KEY, data.impersonator_email);
      localStorage.setItem(TOKEN_KEY, data.token);
      setImpersonator(data.impersonator_email);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const stopImpersonation = async () => {
    const adminToken = sessionStorage.getItem(ADMIN_BACKUP_KEY);
    if (!adminToken) {
      logout();
      return;
    }
    localStorage.setItem(TOKEN_KEY, adminToken);
    sessionStorage.removeItem(ADMIN_BACKUP_KEY);
    sessionStorage.removeItem(IMPERSONATOR_KEY);
    setImpersonator(null);
    await refresh();
    window.location.href = "/admin";
  };

  return (
    <AuthContext.Provider value={{
      user, ready, impersonator,
      login, register, logout, refresh, setUser,
      impersonate, stopImpersonation,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
